import React, { useState } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Grid, List, ListItem, ListItemText,
  TextField, Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

function getAuthHeaders() {
  const token = sessionStorage.getItem('sessionToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function DynamicPrompts() {
  const [matchId, setMatchId] = useState('');
  const [loading, setLoading] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [error, setError] = useState('');

  const fetchPrompts = async () => {
    if (!matchId) return;
    setLoading(true);
    setError('');
    setPrompts([]);
    try {
      const chatUrl = (BASE_URL || '').replace(/\/api\/?$/, '').replace(':3000', ':3001');
      const res = await fetch(`${chatUrl}/admin/ai-chat/${matchId}/dynamic-prompts`, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.success && data.data?.prompts) {
        setPrompts(data.data.prompts);
      } else {
        setError(data.error || 'No prompts generated');
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const intentColors = {
    match_explainer: '#4CAF50', match_narrative: '#2196F3', turning_point: '#FF9800',
    prediction_engine: '#F44336', player_performance: '#9C27B0', player_comparison: '#00BCD4',
    meme_mode: '#E91E63', partnership: '#795548', phase_analysis: '#607D8B',
    bowling_spell: '#3F51B5', event_lookup: '#FF5722', captaincy: '#009688',
    fan_pulse: '#673AB7',
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
        <Typography variant="h5" fontWeight={700} gutterBottom>
          <AutoAwesomeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Dynamic Prompts
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Context-aware suggested questions generated from live match state. These replace static canned prompts and adapt to what's happening in the match.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            label="Match ID"
            value={matchId}
            onChange={e => setMatchId(e.target.value)}
            size="small"
            sx={{ width: 200 }}
          />
          <Button variant="contained" onClick={fetchPrompts} disabled={loading || !matchId}>
            {loading ? <CircularProgress size={20} /> : 'Generate Prompts'}
          </Button>
        </Box>

        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

        {prompts.length > 0 && (
          <Grid container spacing={2}>
            {prompts.map((p, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Card
                  elevation={1}
                  sx={{
                    borderLeft: `4px solid ${intentColors[p.intent] || '#999'}`,
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 3 },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Chip
                        label={p.intent}
                        size="small"
                        sx={{
                          bgcolor: (intentColors[p.intent] || '#999') + '22',
                          color: intentColors[p.intent] || '#999',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">#{p.priority}</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      "{p.label}"
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
        </div>
      </div>
    </Component>
  );
}

export default DynamicPrompts;
